import { Button, Flex, Spacer, Stack } from '@chakra-ui/react'
import { isEqual } from 'lodash-es'
import { DateTime } from 'luxon'
import { useRouter } from 'next/router'
import { useCallback, useState } from 'react'

import type { VolunteerActivityType } from '@/queries/constants'
import SeriesQ from '@/queries/SeriesQ'
import SessionHostQ from '@/queries/SessionHostQ'
import SessionQ from '@/queries/SessionQ'
import TopicQ from '@/queries/TopicQ'
import UserQ from '@/queries/UserQ'
import VolunteerActivityQ from '@/queries/VolunteerActivityQ'
import VolunteerTeamQ from '@/queries/VolunteerTeamQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import { SQL } from '@/client/query'
import { RPCRefresh } from '@/client/rpc'
import { getSubmitVolunteerActivityFormInputs } from '@/client/services/formInputs'
import { generateSessionPrepSessionOptions } from '@/client/services/formInputs/volunteerActivity'
import {
  VOLUNTEER_ACTIVITY_FORM_SESSION_PREP_ID,
  VOLUNTEER_ACTIVITY_FORM_TYPE_NAME,
} from '@/client/services/formInputs/volunteerActivity/getSubmitVolunteerActivityFormInputs'
import {
  nonVolunteerRoleActivityOptions,
  volunteerTeamActivityOptions,
} from '@/client/services/formInputs/volunteerActivity/internal'
import { hasUserRole } from '@/client/services/user'
import {
  AUTOMATICALLY_LOGGED_VOLUNTEER_ACTIVITIES_SET,
  parseLinksString,
  VOLUNTEER_ACTIVITY_TO_LABEL,
} from '@/client/services/volunteerActivity'

import { useToaster } from '@/components/Toast/useToaster'
import Alert from '@/components/UI/Alert'
import Form from '@/components/UI/Form/Form'
import type { FormErrors, FormValue, FormValues } from '@/components/UI/Form/types'
import Tippy from '@/components/UI/Tippy'

import { REVIEW_BUSINESS_DAYS } from './constants'
import type { VolunteerActivityComponentAttributes } from './types'

export const ACTIVITY_TYPE_QUERY_PARAM = 'type'

const DURATION_ERROR = 'Duration (minutes) specified must be a number.'
const NO_UPDATES_PROVIDED = 'You did not update this activity.'

export function SubmitForm({
  activity = null,
  onSubmit,
  onCancel,
}: {
  activity?: VolunteerActivityComponentAttributes | null
  onSubmit: () => void
  onCancel?: () => void
}): JSX.Element {
  const router = useRouter()
  const query = useQuery()
  const user = useUser()
  const isTutor = hasUserRole(user, 'user:tutor')
  const toast = useToaster()
  const [callbackInProgress, setCallbackInProgress] = useState(false)

  const selectedType =
    router.query[ACTIVITY_TYPE_QUERY_PARAM] != null
      ? (router.query[ACTIVITY_TYPE_QUERY_PARAM] as VolunteerActivityType)
      : (activity?.type ?? null)

  // If user is modifying an existing activity, we use `activity.query` so we can include
  // the session prep session with the existing activity's id.
  const sessionPrepSessionOptions =
    (activity?.query ?? query).many(
      SessionHostQ.where(
        {
          userId: user.id,
          include: [
            {
              association: 'Session',
              required: true,
            },
          ],
          order: [[SessionQ.start_date, 'desc']],
        },
        SQL`
          (${SessionQ.isEligibleForVolunteerHours()}) AND
          (${SessionHostQ.session_prep_volunteer_activity_id} IS NULL) OR
          (${
            activity != null
              ? SQL`${SessionHostQ.session_prep_volunteer_activity_id} = ${VolunteerActivityQ.id}`
              : SQL`FALSE`
          })`
      )
    ) ?? []

  const isUpdateMode = activity != null
  const isApproved = activity?.approved_at != null
  const wasAutomaticallyLogged =
    activity?.type != null && AUTOMATICALLY_LOGGED_VOLUNTEER_ACTIVITIES_SET.has(activity.type)

  const userVolunteerTeams = query.many(UserQ.getActiveVolunteerTeams({ userId: user.id })) ?? []

  const volunteerTeamOptions = volunteerTeamActivityOptions(
    userVolunteerTeams.map(team => `${team(VolunteerTeamQ.slug)}`)
  )

  const nonVolunteerTeamOptions = nonVolunteerRoleActivityOptions(user, activity?.type)

  const formInputs = getSubmitVolunteerActivityFormInputs({
    existingActivity: activity,
    selectedType,
    sessionPrepSessionOptions: generateSessionPrepSessionOptions(
      sessionPrepSessionOptions.map(session => ({
        id: session(SessionQ.id) ?? 0,
        sessionTitle: session(SessionQ.title),
        topicName: `${session.one(SessionQ.getTopic())(TopicQ.name)}`,
        seriesTitle: session.one(SessionQ.getSeries())(SeriesQ.title),
        startDateTime: DateTime.fromISO(`${session(SessionQ.start_date)}`),
      }))
    ),
    volunteerTeamOptions,
    nonVolunteerTeamOptions,
  })

  const onChange = useCallback(
    async (name: string, value: FormValue, _values: FormValues) => {
      if (name === VOLUNTEER_ACTIVITY_FORM_TYPE_NAME) {
        const type =
          value == null || `${value}`.length === 0 ? null : (`${value}` as VolunteerActivityType)

        // If activity type changes, we want to fetch relevant entities so the user can link them
        // e.g. if logging Session Prep, we want user to link relevant session.
        const { [ACTIVITY_TYPE_QUERY_PARAM]: _, ...rest } = router.query
        const typeParam = type != null ? { [ACTIVITY_TYPE_QUERY_PARAM]: type } : {}
        await router.replace({ query: { ...rest, ...typeParam } }, undefined, { scroll: false })
      }
    },
    [router]
  )

  const onValidate = useCallback(
    (values: FormValues) => {
      const {
        type,
        date,
        duration_minutes: durationMinutes,
        description,
        links,
        [VOLUNTEER_ACTIVITY_FORM_SESSION_PREP_ID]: rawSessionPrepSessionId,
      } = values
      const parsedDurationMinutes = Number.parseInt(`${durationMinutes}`, 10)
      if (Number.isNaN(parsedDurationMinutes)) {
        return {
          errors: {
            duration_minutes: DURATION_ERROR,
          },
          message: DURATION_ERROR,
        }
      }

      let parsedLinks: string[] | null = null
      try {
        parsedLinks = parseLinksString(`${links}`)
      } catch (err) {
        const errorMessage = (err as Error).message
        return {
          errors: {
            links: errorMessage,
          },
          message: errorMessage,
        }
      }

      const sessionPrepSessionId =
        rawSessionPrepSessionId != null ? Number.parseInt(`${rawSessionPrepSessionId}`, 10) : null

      if (isUpdateMode) {
        const {
          type: existingType,
          date: existingDate,
          description: existingDescription,
          duration_seconds: existingDurationSeconds,
          links: existingLinks,
          related_entity: existingRelatedEntity,
        } = activity

        const dateInUserTz = DateTime.fromISO(`${date}`).startOf('day')
        const existingDateTime = DateTime.fromJSDate(existingDate)
        const existingSessionPrepSessionId =
          existingRelatedEntity?.type === 'host-session-prep' ? existingRelatedEntity.id : null

        if (
          type === existingType &&
          dateInUserTz.toMillis() === existingDateTime.toMillis() &&
          parsedDurationMinutes * 60 === existingDurationSeconds &&
          description === existingDescription &&
          isEqual(parsedLinks, existingLinks) &&
          sessionPrepSessionId === existingSessionPrepSessionId
        ) {
          return {
            errors: { _: '' } as FormErrors,
            message: NO_UPDATES_PROVIDED,
          }
        }
      }
      return null
    },
    [activity, isUpdateMode]
  )

  const onFormSubmit = useCallback(
    async (values: FormValues) => {
      try {
        const {
          [VOLUNTEER_ACTIVITY_FORM_TYPE_NAME]: activityType,
          date,
          duration_minutes,
          description,
          links,
          [VOLUNTEER_ACTIVITY_FORM_SESSION_PREP_ID]: sessionPrepSessionId,
        } = values
        const dateInUserTz = DateTime.fromISO(`${date}`).startOf('day').toJSDate()
        await RPCRefresh.createOrUpdateVolunteerActivity({
          updateActivityId: activity?.id,
          type: `${activityType}` as VolunteerActivityType,
          date: dateInUserTz,
          duration_minutes: Number.parseInt(`${duration_minutes}`, 10),
          description: `${description}`,
          links: parseLinksString(`${links}`),
          sessionPrepSessionId:
            sessionPrepSessionId != null ? Number.parseInt(`${sessionPrepSessionId}`, 10) : null,
        })
        toast({
          status: 'success',
          title: 'Successfully submitted volunteer activity!',
        })
        onSubmit()
      } finally {
        setCallbackInProgress(false)
      }
    },
    [activity?.id, onSubmit, toast]
  )

  const [onDelete] = useAPIWrapper(
    async () => {
      if (!isUpdateMode) {
        return
      }

      setCallbackInProgress(true)
      try {
        await RPCRefresh.deleteVolunteerActivity({ id: activity?.id ?? 0 })

        toast({
          status: 'success',
          title: 'Successfully deleted volunteer activity!',
        })
      } finally {
        setCallbackInProgress(false)
      }
    },
    [activity?.id, isUpdateMode, toast],
    'Delete Volunteer Activity'
  )

  // Do not allow modifying Coda Hours entries - user should delete and re-submit.
  const formDisabled =
    isApproved || wasAutomaticallyLogged || activity?.type === 'coda-hours' || !isTutor

  const FormFooter: React.FC<{ submitting: boolean }> = ({ submitting }) => (
    <Stack spacing="16px" mt="16px">
      {!isUpdateMode ? (
        <Alert variant="info-blue" preserveFontWeight>
          Manual submissions, form signatures &amp; external verifications may take up to{' '}
          {REVIEW_BUSINESS_DAYS} business days to review.
        </Alert>
      ) : null}
      <Flex justify="space-between">
        {isUpdateMode ? (
          <Button
            variant="outline"
            color="fg.error"
            onClick={onDelete}
            isDisabled={submitting || callbackInProgress || wasAutomaticallyLogged || !isTutor}
          >
            Delete
          </Button>
        ) : null}

        <Spacer />

        <Flex gap="8px">
          {onCancel != null ? (
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          ) : null}
          <Tippy
            disabled={!formDisabled}
            content={
              !isTutor
                ? 'Only tutors can log volunteer hour activities.'
                : isApproved
                  ? 'You cannot modify activities after they have been approved.'
                  : wasAutomaticallyLogged
                    ? 'You cannot modify activities that are automatically logged.'
                    : `You cannot update ${VOLUNTEER_ACTIVITY_TO_LABEL['coda-hours']} activities.`
            }
          >
            <Button
              type="submit"
              isDisabled={formDisabled || !isTutor}
              isLoading={submitting || callbackInProgress}
            >
              {isUpdateMode ? 'Update Activity' : 'Submit Activity'}
            </Button>
          </Tippy>
        </Flex>
      </Flex>
    </Stack>
  )

  return (
    <Form
      name="Volunteer Hours"
      disabled={formDisabled}
      blocks={formInputs}
      onSubmit={onFormSubmit}
      onValidate={onValidate}
      FooterComponent={FormFooter}
      onChange={onChange}
    />
  )
}
